import { Link } from "@tanstack/react-router";
import { Archive, CircleCheck, EyeOff, Shield } from "lucide-react";
import type { ReactNode } from "react";

import type { EventCardTarget } from "components/EventSummaryCard";
import { Badge } from "components/ui/badge";
import { cn } from "utils";

import { formatDateRange } from "./event-dates";

/**
 * The pieces the three event cards of the bold design share: the hero, the
 * row and the tile. They sit beside, not inside, EventSummaryCard, which the
 * administration pages still use unchanged.
 */

/** What a card shows of an event. Both the dashboard and browse can fill it. */
export interface EventCardEvent {
  title: string;
  startDate?: string;
  endDate?: string;
  location?: string | null;
  image?: string | null;
}

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * Joined, not merged: tailwind-merge reads `border-edge` (a width) and
 * `border-lit-edge` (a colour) as the same group and keeps only the last, which
 * would drop the edge's width from an emphasised card.
 */
function joinClasses(...classes: (string | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

/**
 * The card's outer element, which is also its link: the whole card is one
 * target, so there is no link inside a link and one tab stop per event. A card
 * with no target is a plain block that carries the same test id and classes.
 */
export function EventCardShell({
  target,
  className,
  testId,
  variant,
  unpublished = false,
  children,
}: {
  target?: EventCardTarget | null;
  className?: string;
  testId: string;
  variant: "hero" | "row" | "tile";
  unpublished?: boolean;
  children: ReactNode;
}) {
  const shared = {
    "data-testid": testId,
    "data-variant": variant,
    "data-published": unpublished ? "false" : undefined,
  } as const;

  if (target?.page === "public") {
    return (
      <Link
        to="/e/$slug"
        params={{ slug: target.slug }}
        className={joinClasses(className, FOCUS_RING)}
        {...shared}
      >
        {children}
      </Link>
    );
  }
  if (target?.page === "admin") {
    return (
      <Link
        to="/admin/event/$eventId"
        params={{ eventId: target.eventId }}
        className={joinClasses(className, FOCUS_RING)}
        {...shared}
      >
        {children}
      </Link>
    );
  }
  return (
    <div className={className} {...shared}>
      {children}
    </div>
  );
}

/** The two gradients an imageless tile band alternates between. */
const TILE_FILLS = ["bg-dusk-tile-a", "bg-dusk-tile-b"] as const;

/**
 * The fill of an imageless tile band, one of two Dusk gradients (white text
 * goes on it). Taken from the title, so the same event has the same fill on
 * the dashboard and on browse, and across reloads.
 */
export function eventTileFill(title: string): (typeof TILE_FILLS)[number] {
  let hash = 0;
  for (const char of title) hash = (hash * 31 + char.codePointAt(0)!) | 0;
  return TILE_FILLS[Math.abs(hash) % TILE_FILLS.length];
}

export function eventInitial(title: string): string {
  const first = Array.from(title.trim())[0];
  return first ? first.toUpperCase() : "?";
}

/** Dates then place on one wrapping line: "21–22 Oct · Athens". */
export function EventMetaLine({
  event,
  today,
  className,
}: {
  event: EventCardEvent;
  today: string;
  className?: string;
}) {
  const dates = formatDateRange(event.startDate, event.endDate, today);
  const place = event.location?.trim();
  if (!dates && !place) return null;
  return (
    <p className={cn("min-w-0 wrap-anywhere", className)}>
      {dates && <span>{dates}</span>}
      {dates && place && <span aria-hidden="true"> &middot; </span>}
      {place && <span>{place}</span>}
    </p>
  );
}

/*
 * The markers, small enough for a row that has three of them. Same meaning,
 * same words and same colours as the badges of EventSummaryCard.
 */
const CHIP = "px-2.5 py-0.5";

export function RegisteredChip() {
  return (
    <Badge variant="success" className={CHIP}>
      <CircleCheck className="mr-1 h-3 w-3" aria-hidden="true" />
      Registered
    </Badge>
  );
}

export function AdminChip() {
  return (
    <Badge className={cn(CHIP, "bg-hb-purple-fill text-white")}>
      <Shield className="mr-1 h-3 w-3" aria-hidden="true" />
      Admin
    </Badge>
  );
}

export function EndedChip() {
  return (
    <Badge
      variant="outline"
      className={cn(CHIP, "bg-muted text-muted-foreground")}
    >
      Ended
    </Badge>
  );
}

/** An archived event: still readable, closed to registration. */
export function ArchivedChip() {
  return (
    <Badge
      variant="outline"
      className={cn(CHIP, "bg-background text-foreground")}
    >
      <Archive className="mr-1 h-3 w-3" aria-hidden="true" />
      Archived
    </Badge>
  );
}

/** Opaque, because it can sit on a photo. */
export function DraftChip() {
  return (
    <Badge
      variant="outline"
      className={cn(CHIP, "bg-background text-foreground")}
    >
      <EyeOff className="mr-1 h-3 w-3" aria-hidden="true" />
      Unpublished
    </Badge>
  );
}
